import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Note, NoteFolder, NoteTemplate } from '../../types';
import noteTemplates from '../../data/notes/templates';
import { noteService, noteFolderService, realtimeService, FirebaseNote, FirebaseNoteFolder } from '../../services/firebase';
import { clearFirestoreCache, forceRestartFirestore } from '../../config/firebase';
import { auth } from '../../config/firebase';

interface NotesState {
  notes: Note[];
  folders: NoteFolder[];
  templates: NoteTemplate[];
  selectedNote: string | null;
  selectedFolder: NoteFolder | null;
  searchQuery: string;
  filterBy: 'all' | 'pinned' | 'favorites' | 'archived' | 'recent';
  sortBy: 'updated' | 'created' | 'title' | 'subject';
  sortOrder: 'asc' | 'desc';
  viewMode: 'grid' | 'list' | 'kanban';
  isLoading: boolean;
  error: string | null;
  isRealtimeSyncActive: boolean;
}

// Async Thunks
export const fetchNotes = createAsyncThunk(
  'notes/fetchNotes',
  async (_, { rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Kullanıcı oturumu bulunamadı');
      
      const notes = await noteService.getUserNotes(user.uid);
      const archivedNotes = await noteService.getArchivedNotes(user.uid);
      
      return [...(notes || []), ...(archivedNotes || [])];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchFolders = createAsyncThunk(
  'notes/fetchFolders',
  async (_, { rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Kullanıcı oturumu bulunamadı');
      
      const folders = await noteFolderService.getUserFolders(user.uid);
      return folders || [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createNoteAsync = createAsyncThunk(
  'notes/createNote',
  async (noteData: Omit<Note, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'lastEditedBy'>, { rejectWithValue }) => {
    try {
      console.log('createNoteAsync çağrıldı:', noteData);
      const user = auth.currentUser;
      if (!user) {
        console.error('Kullanıcı oturumu bulunamadı');
        throw new Error('Kullanıcı oturumu bulunamadı');
      }
      
      console.log('Kullanıcı bulundu:', user.uid);
      
      const firebaseNoteData: Omit<FirebaseNote, 'id' | 'createdAt' | 'updatedAt'> = {
        ...noteData,
        userId: user.uid,
        version: 1,
        lastEditedBy: user.uid
      };
      
      console.log('Firebase not verisi hazırlandı:', firebaseNoteData);
      
      const noteId = await noteService.createNote(firebaseNoteData);
      if (!noteId) throw new Error('Not oluşturulamadı');
      
      console.log('Not oluşturuldu, ID:', noteId);
      
      return {
        ...noteData,
        id: noteId,
        version: 1,
        lastEditedBy: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as Note;
    } catch (error: any) {
      console.error('createNoteAsync hatası:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateNoteAsync = createAsyncThunk(
  'notes/updateNote',
  async (noteData: Note, { rejectWithValue }) => {
    try {
      console.log('updateNoteAsync çağrıldı:', noteData);
      const user = auth.currentUser;
      if (!user) {
        console.error('Kullanıcı oturumu bulunamadı');
        throw new Error('Kullanıcı oturumu bulunamadı');
      }
      
      console.log('Kullanıcı bulundu:', user.uid);
      
      const firebaseNoteData: Partial<FirebaseNote> = {
        ...noteData,
        lastEditedBy: user.uid
      };
      
      console.log('Firebase güncelleme verisi hazırlandı:', firebaseNoteData);
      
      const success = await noteService.updateNote(noteData.id, firebaseNoteData);
      if (!success) throw new Error('Not güncellenemedi');
      
      console.log('Not güncellendi:', noteData.id);
      
      return noteData;
    } catch (error: any) {
      console.error('updateNoteAsync hatası:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteNoteAsync = createAsyncThunk(
  'notes/deleteNote',
  async (noteId: string, { rejectWithValue }) => {
    try {
      const success = await noteService.deleteNote(noteId);
      if (!success) throw new Error('Not silinemedi');
      
      return noteId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleNotePinnedAsync = createAsyncThunk(
  'notes/toggleNotePinned',
  async (noteId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { notes: NotesState };
      const note = state.notes.notes.find(n => n.id === noteId);
      if (!note) throw new Error('Not bulunamadı');
      
      const newPinnedState = !note.isPinned;
      const success = await noteService.toggleNotePinned(noteId, newPinnedState);
      if (!success) throw new Error('Not sabitleme durumu değiştirilemedi');
      
      return { noteId, isPinned: newPinnedState };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleNoteFavoriteAsync = createAsyncThunk(
  'notes/toggleNoteFavorite',
  async (noteId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { notes: NotesState };
      const note = state.notes.notes.find(n => n.id === noteId);
      if (!note) throw new Error('Not bulunamadı');
      
      const newFavoriteState = !note.isFavorite;
      const success = await noteService.toggleNoteFavorite(noteId, newFavoriteState);
      if (!success) throw new Error('Not favori durumu değiştirilemedi');
      
      return { noteId, isFavorite: newFavoriteState };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const toggleNoteArchivedAsync = createAsyncThunk(
  'notes/toggleNoteArchived',
  async (noteId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { notes: NotesState };
      const note = state.notes.notes.find(n => n.id === noteId);
      if (!note) throw new Error('Not bulunamadı');
      
      const newArchivedState = !note.isArchived;
      const success = await noteService.toggleNoteArchived(noteId, newArchivedState);
      if (!success) throw new Error('Not arşiv durumu değiştirilemedi');
      
      return { noteId, isArchived: newArchivedState };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createFolderAsync = createAsyncThunk(
  'notes/createFolder',
  async (folderData: Omit<NoteFolder, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Kullanıcı oturumu bulunamadı');
      
      const firebaseFolderData: Omit<FirebaseNoteFolder, 'id' | 'createdAt' | 'updatedAt'> = {
        ...folderData,
        userId: user.uid
      };
      
      const folderId = await noteFolderService.createFolder(firebaseFolderData);
      if (!folderId) throw new Error('Klasör oluşturulamadı');
      
      return {
        ...folderData,
        id: folderId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as NoteFolder;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Realtime sync manager - stores listeners outside Redux
class RealtimeSyncManager {
  private notesUnsubscribe: (() => void) | null = null;
  private foldersUnsubscribe: (() => void) | null = null;
  private isStarting = false;
  
  async startSync(userId: string, dispatch: any) {
    // Prevent multiple simultaneous starts
    if (this.isStarting) return;
    this.isStarting = true;
    
    // Stop existing listeners if any
    this.stopSync();
    
    try {
      // Start listening to notes with error handling
      this.notesUnsubscribe = realtimeService.listenToNotes(userId, 
        (firebaseNotes) => {
          try {
            const appNotes: Note[] = firebaseNotes.map(note => ({
              ...note,
              createdAt: new Date(note.createdAt).toISOString(),
              updatedAt: new Date(note.updatedAt).toISOString()
            }));
            dispatch(setNotesFromRealtime(appNotes));
          } catch (error) {
            console.error('Error processing notes data:', error);
          }
        }
      );
      
      // Start listening to folders with error handling
      this.foldersUnsubscribe = realtimeService.listenToNoteFolders(userId, 
        (firebaseFolders) => {
          try {
            const appFolders: NoteFolder[] = firebaseFolders.map(folder => ({
              ...folder,
              createdAt: new Date(folder.createdAt).toISOString(),
              updatedAt: new Date(folder.updatedAt).toISOString()
            }));
            dispatch(setFoldersFromRealtime(appFolders));
          } catch (error) {
            console.error('Error processing folders data:', error);
          }
        }
      );
    } catch (error) {
        console.error('Error starting sync:', error);
        // Handle critical Firestore errors
        if (error instanceof Error && (
          error.message.includes('INTERNAL ASSERTION FAILED') ||
          error.message.includes('client has already been terminated')
        )) {
          console.warn('🚨 Critical Firestore error in sync - forcing restart...', error.message);
          try {
            await forceRestartFirestore();
            console.log('✅ Firestore restarted, retrying sync...');
            // Retry sync after restart
            setTimeout(() => {
              if (auth.currentUser) {
                this.startSync(auth.currentUser.uid, dispatch);
              }
            }, 2000);
          } catch (restartError) {
            console.error('Failed to restart Firestore:', restartError);
            await clearFirestoreCache();
          }
        }
        this.stopSync();
      } finally {
      this.isStarting = false;
    }
  }
  
  stopSync() {
    try {
      if (this.notesUnsubscribe) {
        this.notesUnsubscribe();
        this.notesUnsubscribe = null;
      }
      if (this.foldersUnsubscribe) {
        this.foldersUnsubscribe();
        this.foldersUnsubscribe = null;
      }
    } catch (error) {
      console.error('Error stopping sync:', error);
    } finally {
      this.isStarting = false;
    }
  }
  
  isActive() {
    return this.notesUnsubscribe !== null || this.foldersUnsubscribe !== null;
  }
}

const realtimeSyncManager = new RealtimeSyncManager();

// Realtime sync thunks
export const startRealtimeSync = createAsyncThunk(
  'notes/startRealtimeSync',
  async (_, { dispatch }) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Kullanıcı oturumu bulunamadı');
    
    await realtimeSyncManager.startSync(user.uid, dispatch);
    dispatch(setRealtimeSyncActive(true));
    
    return { success: true };
  }
);

export const stopRealtimeSync = createAsyncThunk(
  'notes/stopRealtimeSync',
  async (_, { dispatch }) => {
    realtimeSyncManager.stopSync();
    dispatch(setRealtimeSyncActive(false));
    
    return { success: true };
  }
);

const initialState: NotesState = {
  notes: [],
  folders: [],
  templates: noteTemplates,
  selectedNote: null,
  selectedFolder: null,
  searchQuery: '',
  filterBy: 'all',
  sortBy: 'updated',
  sortOrder: 'desc',
  viewMode: 'grid',
  isLoading: false,
  error: null,
  isRealtimeSyncActive: false
};

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    // Local state operations (for immediate UI updates)
    createNote: (state, action: PayloadAction<Omit<Note, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'lastEditedBy'>>) => {
      const newNote: Note = {
        ...action.payload,
        id: Date.now().toString(),
        version: 1,
        lastEditedBy: 'current-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      state.notes.unshift(newNote);
    },

    updateNote: (state, action: PayloadAction<Note>) => {
      const updatedNote = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === updatedNote.id);
      if (noteIndex !== -1) {
        state.notes[noteIndex] = updatedNote;
      }
    },

    deleteNote: (state, action: PayloadAction<string>) => {
      state.notes = state.notes.filter(note => note.id !== action.payload);
      if (state.selectedNote === action.payload) {
        state.selectedNote = null;
      }
    },

    duplicateNote: (state, action: PayloadAction<string>) => {
      const originalNote = state.notes.find(note => note.id === action.payload);
      if (originalNote) {
        const duplicatedNote: Note = {
          ...originalNote,
          id: Date.now().toString(),
          title: `${originalNote.title} (Kopya)`,
          version: 1,
          lastEditedBy: 'current-user',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        state.notes.unshift(duplicatedNote);
      }
    },

    toggleNotePinned: (state, action: PayloadAction<string>) => {
      const note = state.notes.find(note => note.id === action.payload);
      if (note) {
        note.isPinned = !note.isPinned;
        note.updatedAt = new Date().toISOString();
      }
    },

    toggleNoteFavorite: (state, action: PayloadAction<string>) => {
      const note = state.notes.find(note => note.id === action.payload);
      if (note) {
        note.isFavorite = !note.isFavorite;
        note.updatedAt = new Date().toISOString();
      }
    },

    toggleNoteArchived: (state, action: PayloadAction<string>) => {
      const note = state.notes.find(note => note.id === action.payload);
      if (note) {
        note.isArchived = !note.isArchived;
        note.updatedAt = new Date().toISOString();
      }
    },

    addNoteTag: (state, action: PayloadAction<{ noteId: string; tag: string }>) => {
      const { noteId, tag } = action.payload;
      const note = state.notes.find(note => note.id === noteId);
      if (note && !note.tags.includes(tag)) {
        note.tags.push(tag);
        note.updatedAt = new Date().toISOString();
      }
    },

    removeNoteTag: (state, action: PayloadAction<{ noteId: string; tag: string }>) => {
      const { noteId, tag } = action.payload;
      const note = state.notes.find(note => note.id === noteId);
      if (note) {
        note.tags = note.tags.filter(t => t !== tag);
        note.updatedAt = new Date().toISOString();
      }
    },

    // Folder operations
    createFolder: (state, action: PayloadAction<Omit<NoteFolder, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const newFolder: NoteFolder = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      state.folders.push(newFolder);
    },

    updateFolder: (state, action: PayloadAction<{ id: string; updates: Partial<NoteFolder> }>) => {
      const { id, updates } = action.payload;
      const folderIndex = state.folders.findIndex(folder => folder.id === id);
      if (folderIndex !== -1) {
        state.folders[folderIndex] = {
          ...state.folders[folderIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
    },

    deleteFolder: (state, action: PayloadAction<string>) => {
      state.folders = state.folders.filter(folder => folder.id !== action.payload);
      if (state.selectedFolder?.id === action.payload) {
        state.selectedFolder = null;
      }
      // Move notes from deleted folder back to root
      state.notes.forEach(note => {
        if (note.folderId === action.payload) {
          note.folderId = undefined;
          note.updatedAt = new Date().toISOString();
        }
      });
    },

    addNoteToFolder: (state, action: PayloadAction<{ folderId: string; noteId: string }>) => {
      const { folderId, noteId } = action.payload;
      const folder = state.folders.find(folder => folder.id === folderId);
      if (folder && !folder.noteIds.includes(noteId)) {
        folder.noteIds.push(noteId);
        folder.updatedAt = new Date().toISOString();
      }
    },

    removeNoteFromFolder: (state, action: PayloadAction<{ folderId: string; noteId: string }>) => {
      const { folderId, noteId } = action.payload;
      const folder = state.folders.find(folder => folder.id === folderId);
      if (folder) {
        folder.noteIds = folder.noteIds.filter(id => id !== noteId);
        folder.updatedAt = new Date().toISOString();
      }
    },

    // Selection
    selectNote: (state, action: PayloadAction<string | null>) => {
      state.selectedNote = action.payload;
    },

    selectFolder: (state, action: PayloadAction<string | null>) => {
      const folderId = action.payload;
      state.selectedFolder = folderId ? state.folders.find(f => f.id === folderId) || null : null;
    },

    // Search and filter
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    setFilterBy: (state, action: PayloadAction<NotesState['filterBy']>) => {
      state.filterBy = action.payload;
    },

    setSortBy: (state, action: PayloadAction<NotesState['sortBy']>) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action: PayloadAction<NotesState['sortOrder']>) => {
      state.sortOrder = action.payload;
    },

    setViewMode: (state, action: PayloadAction<NotesState['viewMode']>) => {
      state.viewMode = action.payload;
    },

    // Loading and error
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Realtime sync actions
    setNotesFromRealtime: (state, action: PayloadAction<Note[]>) => {
      state.notes = action.payload;
    },

    setFoldersFromRealtime: (state, action: PayloadAction<NoteFolder[]>) => {
      state.folders = action.payload;
    },

    setRealtimeSyncActive: (state, action: PayloadAction<boolean>) => {
      state.isRealtimeSyncActive = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch Notes
    builder
      .addCase(fetchNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notes = action.payload;
      })
      .addCase(fetchNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Folders
      .addCase(fetchFolders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.folders = action.payload;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Note
      .addCase(createNoteAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createNoteAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notes.unshift(action.payload);
      })
      .addCase(createNoteAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Note
      .addCase(updateNoteAsync.fulfilled, (state, action) => {
        const index = state.notes.findIndex(note => note.id === action.payload.id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        }
      })
      // Delete Note
      .addCase(deleteNoteAsync.fulfilled, (state, action) => {
        state.notes = state.notes.filter(note => note.id !== action.payload);
        if (state.selectedNote === action.payload) {
          state.selectedNote = null;
        }
      })
      // Toggle Operations
      .addCase(toggleNotePinnedAsync.fulfilled, (state, action) => {
        const note = state.notes.find(note => note.id === action.payload.noteId);
        if (note) {
          note.isPinned = action.payload.isPinned;
          note.updatedAt = new Date().toISOString();
        }
      })
      .addCase(toggleNoteFavoriteAsync.fulfilled, (state, action) => {
        const note = state.notes.find(note => note.id === action.payload.noteId);
        if (note) {
          note.isFavorite = action.payload.isFavorite;
          note.updatedAt = new Date().toISOString();
        }
      })
      .addCase(toggleNoteArchivedAsync.fulfilled, (state, action) => {
        const note = state.notes.find(note => note.id === action.payload.noteId);
        if (note) {
          note.isArchived = action.payload.isArchived;
          note.updatedAt = new Date().toISOString();
        }
      })
      // Create Folder
      .addCase(createFolderAsync.fulfilled, (state, action) => {
        state.folders.push(action.payload);
      });
  }
});

export const {
  createNote,
  updateNote,
  deleteNote,
  duplicateNote,
  toggleNotePinned,
  toggleNoteFavorite,
  toggleNoteArchived,
  addNoteTag,
  removeNoteTag,
  createFolder,
  updateFolder,
  deleteFolder,
  addNoteToFolder,
  removeNoteFromFolder,
  selectNote,
  selectFolder,
  setSearchQuery,
  setFilterBy,
  setSortBy,
  setSortOrder,
  setViewMode,
  setLoading,
  setError,
  setNotesFromRealtime,
  setFoldersFromRealtime,
  setRealtimeSyncActive
} = notesSlice.actions;

// Async thunks are already exported above with 'export const'

export default notesSlice.reducer;